import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useAuth() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check initial session
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setIsLoggedIn(true);
          setUserEmail(session.user.email || null);
          setUserId(session.user.id);
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
      }
    };
    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setIsLoggedIn(true);
        setUserEmail(session.user.email || null);
        setUserId(session.user.id);
      } else {
        setIsLoggedIn(false);
        setUserEmail(null);
        setUserId(null);
      }
      setLoading(false);
    });

    // Auto-refresh token every 50 minutes (before 1h expiry)
    // This keeps the user logged in indefinitely
    const refreshInterval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            console.error('Token refresh error:', error);
          } else {
            console.log('🔄 Token auto-refreshed at', new Date().toLocaleTimeString());
          }
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setUserEmail(null);
    setUserId(null);
  };

  return {
    isLoggedIn,
    userEmail,
    userId,
    loading,
    logout,
  };
}
